import type { MemberAssociationResponse } from "@finn-no/mobility-dealer-types";
import { Trans } from "@lingui/react";
import { BrandURL, type MobilityAd } from "@mobility-item-page/types";
import {
  isBoatAd,
  isCarAd,
  isCaravanAd,
  isLeasing,
  isMobileHomeAd,
  isMotorbikeAd,
} from "@mobility-item-page/utilities";
import { ElementType, Intent, TargetType } from "@mobility-item-page/utilities/client";
import { MemberAssociationTabs, type SafetyTabs } from "src/types/safetyElements.ts";
import { useAppConfig } from "../../../../context/AppConfigProviderContext.tsx";
import usePulse from "../../../../utils/pulse-tracking/usePulse.ts";
import { Icon } from "../../Icon.tsx";

type Props = {
  adData: MobilityAd;
  tradeOrg?: SafetyTabs;
  memberAssociations?: MemberAssociationResponse[];
};

const TradeOrg = ({ adData, tradeOrg, memberAssociations }: Props) => {
  const { brand } = useAppConfig();
  const { trackClick } = usePulse();
  const host = BrandURL[brand as keyof typeof BrandURL];

  const trackLinkClick = ({
    targetTitle,
    linkUrl,
    targetType,
  }: {
    targetTitle: string;
    linkUrl: string;
    targetType: TargetType.Page | TargetType.ExternalContent;
  }) => {
    trackClick({
      name: "Read about member association",
      objectId: "MO_ev98",
      elementType: ElementType.Link,
      intent: Intent.Open,
      position: "Safety elements",
      target: {
        id: linkUrl,
        type: targetType,
        name: targetTitle,
      },
    });
  };

  if (memberAssociations && memberAssociations.length > 0) {
    return (
      <>
        {memberAssociations.map((association) => {
          const url = association.link?.url;
          const externalLink = url ? new URL(url).host.replace(/^(?:www|local|dev)\./, "") !== host : false;
          return (
            <div className="mb-16" key={association.title}>
              <p className="mt-16 md:mt-0 mb-0">{association.description}</p>
              {url && (
                <p className="mt-16 md:mt-8 mb-0">
                  <a
                    className="flex items-center gap-4"
                    href={url}
                    onClick={() =>
                      trackLinkClick({
                        targetTitle: association.title,
                        linkUrl: url,
                        targetType: externalLink ? TargetType.ExternalContent : TargetType.Page,
                      })
                    }
                  >
                    {association.link?.text}
                    {externalLink && <Icon name={"LinkExternal"} />}
                  </a>
                </p>
              )}
            </div>
          );
        })}
      </>
    );
  }

  if (isCarAd(adData)) {
    if (tradeOrg === MemberAssociationTabs.NbfMember) {
      return (
        <>
          <p className="mt-16 md:mt-0 mb-0">
            {isLeasing(adData.adViewType) ? (
              <Trans
                id="itemPageLayout.safetyElement.tradeOrg.NBF.info.leasing"
                message="The car is leased from a member of the Norges Bilbransjeforbund (NBF). When you lease a car from a dealer who is a member of NBF, you are leasing from a dealer who has committed to following NBF's ethical guidelines. This provides you as a buyer with extra security."
                comment="Do not edit the translation submitted. Pre-approved text, must not deviate."
              />
            ) : (
              <Trans
                id="itemPageLayout.safetyElement.tradeOrg.NBF.info"
                message="The car is sold by a member of the Norges Bilbransjeforbund (NBF). When you buy a car from a dealer who is a member
					of NBF, you are buying from a dealer who has committed to following NBF's ethical guidelines. This provides
					you as a buyer with extra security."
                comment="Do not edit the translation submitted. Pre-approved text, must not deviate."
              />
            )}
          </p>
          <p className="mt-16 md:mt-8 mb-0">
            <a
              href="/mobility/nbf"
              onClick={() =>
                trackLinkClick({
                  targetTitle: "NBF member benefits",
                  linkUrl: "/mobility/nbf",
                  targetType: TargetType.Page,
                })
              }
            >
              <Trans
                id="itemPageLayout.safetyElement.tradeOrg.NBF.readmore"
                message="More about the benefits of buying from an NBF member."
                comment="Do not edit the translation submitted. Pre-approved text, must not deviate."
              />
            </a>
          </p>
        </>
      );
    }
    if (tradeOrg === MemberAssociationTabs.MobilityPoolMember) {
      return (
        <>
          <p className="mt-16 md:mt-0 mb-0">
            {isLeasing(adData.adViewType) ? (
              <Trans
                id="itemPageLayout.safetyElement.tradeOrg.mobilityPool.info.leasing"
                message="The car is leased by a member of Mobility Pool. When you lease a car from a dealer who is a member of Mobility Pool, you are leasing from a dealer who has the opportunity to offer Mobility Pool's very good benefit agreements."
                comment="Do not edit the translation submitted. Pre-approved text, must not deviate."
              />
            ) : (
              <Trans
                id="itemPageLayout.safetyElement.tradeOrg.mobilityPool.info"
                message="The car is sold by a member of Mobility Pool. When you buy a car from a dealer who is a member of Mobility Pool, you are buying from a dealer who has the opportunity to offer Mobility Pool's very good benefit agreements."
                comment="Do not edit the translation submitted. Pre-approved text, must not deviate."
              />
            )}
          </p>
          <p className="mt-16 md:mt-8 mb-0">
            <a
              href="/mobility/mobilitypool"
              onClick={() =>
                trackLinkClick({
                  targetTitle: "Mobility Pool member benefits",
                  linkUrl: "/mobility/mobilitypool",
                  targetType: TargetType.Page,
                })
              }
            >
              <Trans
                id="itemPageLayout.safetyElement.tradeOrg.mobilityPool.readmore"
                message="More about the benefits of shopping by a member of the Mobility Pool."
                comment="Do not edit the translation submitted. Pre-approved text, must not deviate."
              />
            </a>
          </p>
        </>
      );
    }
    if (tradeOrg === MemberAssociationTabs.BruktbilgruppenMember) {
      return (
        <>
          <p className="mt-16 md:mt-0 mb-0">
            {isLeasing(adData.adViewType) ? (
              <Trans
                id="itemPageLayout.safetyElement.tradeOrg.bruktbilgruppen.info.leasing"
                message="The car is leased by a certified member of the Bruktbilgruppen. When you lease a car from a dealer who is a certified member of the Bruktbilgruppen, you are leasing from a dealer who has undertaken to follow the Bruktbilgruppen's ethical guidelines and who has undergone a certification course. This gives you as a leasee extra peace of mind."
                comment="Do not edit the translation submitted. Pre-approved text, must not deviate."
              />
            ) : (
              <Trans
                id="itemPageLayout.safetyElement.tradeOrg.bruktbilgruppen.info"
                message="The car is sold by a certified member of the Bruktbilgruppen. When you buy a car from a dealer who is a certified member of the Bruktbilgruppen, you are buying from a dealer who has undertaken to follow the Bruktbilgruppen's ethical guidelines and who has undergone a certification course. This gives you as a buyer extra peace of mind."
                comment="Do not edit the translation submitted. Pre-approved text, must not deviate."
              />
            )}
          </p>
          <p className="mt-16 md:mt-8 mb-0">
            <a
              href="/mobility/bruktbilgruppen"
              onClick={() =>
                trackLinkClick({
                  targetTitle: "Bruktbilgruppen member benefits",
                  linkUrl: "/mobility/bruktbilgruppen",
                  targetType: TargetType.Page,
                })
              }
            >
              <Trans
                id="itemPageLayout.safetyElement.tradeOrg.bruktbilgruppen.readmore"
                message="More about the benefits of buying from a certified member of the Bruktbilgruppen."
                comment="Do not edit the translation submitted. Pre-approved text, must not deviate."
              />
            </a>
          </p>
        </>
      );
    }
  }
  if (isBoatAd(adData)) {
    return (
      <>
        <p className="mt-16 md:mt-0 mb-0">
          <Trans
            id="itemPageLayout.safetyElement.tradeOrg.norboat.info"
            message="Sold by a member of Norboat. When you buy a product or service from a Norboat member, you know that they are associated with a trade organization that works to create the best possible framework conditions for the boating industry and boating based on safe boat ownership."
            comment="Do not edit the translation submitted. Pre-approved text, must not deviate."
          />
        </p>
        <p className="mt-16 md:mt-8 mb-0">
          <a
            href="/mobility/norboat"
            onClick={() =>
              trackLinkClick({
                targetTitle: "Norboat member benefits",
                linkUrl: "/mobility/norboat",
                targetType: TargetType.Page,
              })
            }
          >
            <Trans
              id="itemPageLayout.safetyElement.tradeOrg.norboat.readmore"
              message="More about the benefits of buying from a Norboat member."
              comment="Do not edit the translation submitted. Pre-approved text, must not deviate."
            />
          </a>
        </p>
      </>
    );
  }
  if (isMobileHomeAd(adData)) {
    if (tradeOrg === MemberAssociationTabs.NcbMember) {
      return (
        <>
          <p className="mt-16 md:mt-0 mb-0">
            <Trans
              id="itemPageLayout.safetyElement.tradeOrg.ncb.mobileHome.info"
              message="The motorhome is sold by a member of NCB. When you buy a motorhome from a dealer who is a member of NCB, you are buying from a dealer who has undertaken to follow NCB's ethical guidelines. This gives you as a buyer extra peace of mind."
              comment="Do not edit the translation submitted. Pre-approved text, must not deviate."
            />
          </p>
          <p className="mt-16 md:mt-8 mb-0">
            <a
              href="/mobility/ncb"
              onClick={() =>
                trackLinkClick({
                  targetTitle: "NCB member benefits",
                  linkUrl: "/mobility/ncb",
                  targetType: TargetType.Page,
                })
              }
            >
              <Trans
                id="itemPageLayout.safetyElement.tradeOrg.ncb.readmore"
                message="More about the benefits of buying from an NCB member."
                comment="Do not edit the translation submitted. Pre-approved text, must not deviate."
              />
            </a>
          </p>
        </>
      );
    }
  }
  if (isCaravanAd(adData)) {
    if (tradeOrg === MemberAssociationTabs.NcbMember) {
      return (
        <>
          <p className="mt-16 md:mt-0 mb-0">
            <Trans
              id="itemPageLayout.safetyElement.tradeOrg.ncb.caravan.info"
              message="The caravan is sold by a member of NCB. When you buy a caravan from a dealer who is a member of NCB, you are buying from a dealer who has undertaken to follow NCB's ethical guidelines. This gives you as a buyer extra peace of mind."
              comment="Do not edit the translation submitted. Pre-approved text, must not deviate."
            />
          </p>
          <p className="mt-16 md:mt-8 mb-0">
            <a
              href="/mobility/ncb"
              onClick={() =>
                trackLinkClick({
                  targetTitle: "NCB member benefits",
                  linkUrl: "/mobility/ncb",
                  targetType: TargetType.Page,
                })
              }
            >
              <Trans
                id="itemPageLayout.safetyElement.tradeOrg.ncb.readmore"
                message="More about the benefits of buying from an NCB member."
                comment="Do not edit the translation submitted. Pre-approved text, must not deviate."
              />
            </a>
          </p>
        </>
      );
    }
  }
  if (isMotorbikeAd(adData)) {
    if (tradeOrg === MemberAssociationTabs.NmcfMember) {
      return (
        <>
          <p className="mt-16 md:mt-0 mb-0">
            <Trans
              id="itemPageLayout.safetyElement.tradeOrg.nmcf.info"
              message="The motorcycle is sold by a member of the Norwegian Motorcycle Dealers Association. When you buy MC from a dealer who is a member of the NMCF, you are buying from a dealer who has committed to following the NMCF ethical guidelines. This gives you as a buyer extra peace of mind."
              comment="Do not edit the translation submitted. Pre-approved text, must not deviate."
            />
          </p>
          <p className="mt-16 md:mt-8 mb-0">
            <a
              href="/mobility/nmcf"
              onClick={() =>
                trackLinkClick({
                  targetTitle: "NMCF member benefits",
                  linkUrl: "/mobility/nmcf",
                  targetType: TargetType.Page,
                })
              }
            >
              <Trans
                id="itemPageLayout.safetyElement.tradeOrg.nmcf.readmore"
                message="More about the benefits of buying from a NMCF member."
                comment="Do not edit the translation submitted. Pre-approved text, must not deviate."
              />
            </a>
          </p>
        </>
      );
    }
  }

  return null;
};

export default TradeOrg;
